import axios from 'axios';
import * as Immutable from 'immutable';
import isIp from 'is-ip';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import url from 'url';
import * as FlowPlanUtils from 'utils/flow-plan-utils';
import Logger from 'utils/logger';

import type {UrlWithStringQuery} from 'node:url';
import type {FetchCaptureHostResponse} from 'api/exams-api';
import type {Map as ImmutableMap, List, MapOf} from 'immutable';
import type {FlowPlanPhase} from 'types/flow-plan-types';
import type {CaptureHost, CaptureInput, Channel, Ptz} from 'views/signed-in/sessions/stores/live-session-store';
import type {SessionViewerStore} from 'views/signed-in/sessions/stores/session-viewer-store';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ImmutableList = List<any>;

const DEFAULT_INPUTS_LIMIT = 4;

const logger = new Logger('recording-utils');

export const isIOS = () => {
    return /iPad|iPhone|iPod|BLineApp/.test(navigator.userAgent) && !window.MSStream && isNil(window.chrome);
};

const selectHost = (hosts: List<string> | null) => {
    if (isNil(hosts) || hosts.isEmpty()) {
        return '';
    }

    const urlHosts = hosts.map((h) => url.parse(h));

    let host = urlHosts.find((h) => h.protocol === 'https');
    if (!isNil(host)) {
        return host;
    }

    host = urlHosts.find((h) => h.hostname.endsWith('.direct'));
    if (!isNil(host)) {
        return host;
    }

    host = urlHosts.find((h) => isIp(h.hostname));
    return !isNil(host) ? host : urlHosts.get(0);
};

export const isValidChannel = (channel: Channel): boolean => {
    const status = channel.get('status');
    return status === 'recording' || status === 'streaming';
};

const isValidCaptureInput = (captureInput: CaptureInput): boolean => {
    return captureInput.get('videoStatus') === 'ready' && !captureInput.get('channels', Immutable.List()).isEmpty();
};

export const getValidCaptureInputs = (captureHost: CaptureHost | FetchCaptureHostResponse): List<CaptureInput> => {
    logger.info('[videoInfo] Getting valid capture inputs for', JSON.stringify(captureHost.toJS()));
    const result = captureHost
        .getIn(['captureNode', 'captureInputs'], Immutable.List<CaptureInput>())
        .filter((ci) => isValidCaptureInput(ci));
    logger.info('[videoInfo] Valid capture inputs:', JSON.stringify(result.toJS()));
    return result;
};

export const getChannels = (captureHost: CaptureHost | FetchCaptureHostResponse): List<Channel> => {
    return getValidCaptureInputs(captureHost).reduce<List<Channel>>((a, ci) => {
        return a.concat(ci.get('channels', Immutable.List()).map((ch) => ch.set('captureInputId', ci)));
    }, Immutable.List<Channel>());
};

export const getRecordedFilesFromRecordings = (recordings) => {
    return recordings
        .reduce((a, r) => {
            const recordedFiles = r.getIn(['vcRecording', 'recordedFiles'], Immutable.List());
            return a.concat(recordedFiles);
        }, Immutable.List())
        .sortBy((r) => r.get('sort', 0));
};

const getRecordedFiles = (session) => {
    return getRecordedFilesFromRecordings(session.get('recordings', Immutable.List()));
};

const getStreamingChannels = (captureHost: CaptureHost) => {
    return getChannels(captureHost).filter((ch) => ch.get('status') === 'streaming');
};

const getRecordingChannels = (captureHost) => {
    return getChannels(captureHost).filter((ch) => ch.get('status') === 'recording');
};

export const deleteVideosRequestMade = (session) => {
    if (session.has('recordings')) {
        const recordedFiles = getRecordedFiles(session);
        return !recordedFiles.isEmpty() && recordedFiles.every((r) => !isNil(r.get('cloudDeleteRequestTs')));
    }
    return false;
};

export const noVideosRecorded = (session) => {
    if (session.has('recordings')) {
        const recordings = session.get('recordings', Immutable.List());
        if (recordings.isEmpty()) {
            return true;
        }
        return recordings.reduce((a, r) => {
            const recordedFiles = r.getIn(['vcRecording', 'recordedFiles'], Immutable.List());
            return a || recordedFiles.isEmpty();
        }, false);
    }
    return false;
};

const isRecordedFileReady = (live) => {
    return (recordedFile) => {
        const isNotDeleted = isNil(recordedFile.get('cloudDeleteRequestTs'));

        //TODO: this will fail for WebRTC-only CNs (browser)
        return (
            // live sessions will only create recordedFiles for feeds that are up
            (live ||
                (!isNil(recordedFile.get('localRtmp')) && !recordedFile.get('localRtmp').isEmpty()) ||
                (!isNil(recordedFile.get('localHttp')) && !recordedFile.get('localHttp').isEmpty()) ||
                (!isNil(recordedFile.get('remoteRtmp')) && !recordedFile.get('remoteRtmp').isEmpty()) ||
                (!isNil(recordedFile.get('remoteHttp')) && !recordedFile.get('remoteHttp').isEmpty()) ||
                (!isNil(recordedFile.get('remoteHls')) && !recordedFile.get('remoteHls').isEmpty())) &&
            isNotDeleted
        );
    };
};

const isRecordedFileReadyOnLocalOnly = (recordedFile) => {
    const isNotDeleted = isNil(recordedFile.get('cloudDeleteRequestTs'));

    const hasLocalStreams =
        (!isEmpty(recordedFile.get('localRtmp')) || !isEmpty(recordedFile.get('localHttp'))) && isNotDeleted;

    return recordedFile.get('uploadStatus') !== 'success' && hasLocalStreams;
};

export const areVideoStreamsReady = async (session) => {
    if (isNil(session) || isNil(session.get('recordings')) || session.get('recordings').isEmpty()) {
        return false;
    }

    const recordedFiles = getRecordedFiles(session);
    const live = session.get('live', false);
    let hasLocal = false;
    if (!live && recordedFiles.some(isRecordedFileReadyOnLocalOnly) && !deleteVideosRequestMade(session)) {
        hasLocal = await detectLocalStreamAvailability(recordedFiles.first().get('localHttp'));
    }
    return hasLocal || (recordedFiles.some(isRecordedFileReady(live)) && !deleteVideosRequestMade(session));
};

/**
 * From a session with a list of recordings, return only the recordings where
 * at least some of the files are ready. Used to identify which recordings are
 * valid follow-ups.
 */
export const getRecordingsWithFilesReady = (session: ImmutableMap<unknown, unknown>): ImmutableList => {
    // @ts-expect-error TS2339: Property filter does not exist on type unknown
    const readyRecordedFiles = session.get('recordings', Immutable.List()).filter((recording) => {
        const recordedFiles = recording.getIn(['vcRecording', 'recordedFiles'], Immutable.List());
        return recordedFiles.some(isRecordedFileReady(session.get('live', false)));
    });

    const examFlowPlan = getExamFlowFromSession(session);

    return isNil(examFlowPlan) ? readyRecordedFiles : backfillRecordingOrder(readyRecordedFiles, examFlowPlan);
};

/**
 * Returns an examFlowPlan from a session, if it exists.
 */
export const getExamFlowFromSession = (session: ImmutableMap<unknown, unknown>): FlowPlanPhase[][] | null => {
    const examFlowPlan = session?.getIn(['scenario', 'examFlow', 'flowPlan']);
    return !isNil(examFlowPlan)
        ? FlowPlanUtils.getFlowPlanBlocks(
              // @ts-expect-error TS2339: Property toJS does not exist on type unknown
              examFlowPlan.toJS(),
          )
        : null;
};

/**
 * Backfills the recording phase order by adding encounters that have recording disabled.
 */
export const backfillRecordingOrder = (
    recordingsWithFilesReady: ImmutableList,
    examFlowPlan: FlowPlanPhase[][],
): ImmutableList => {
    recordingsWithFilesReady = recordingsWithFilesReady.sortBy((r) => r.getIn(['examPhase', 'examFlowBlockId']));
    examFlowPlan.forEach((block, index) => {
        const blockRecording = recordingsWithFilesReady.get(index);
        const blockRecordingIndex = blockRecording?.getIn(['examPhase', 'examFlowBlockId']);
        if (isNil(blockRecording) || (!isNil(blockRecordingIndex) && blockRecordingIndex !== index)) {
            recordingsWithFilesReady = recordingsWithFilesReady.splice(
                index,
                0,
                Immutable.Map({
                    examPhase: Immutable.Map({
                        examFlowBlockId: index,
                    }),
                }),
            );
        }
    });
    return recordingsWithFilesReady;
};

export const getReadyRecordedFiles = (session) => {
    return getRecordedFiles(session).filter(isRecordedFileReady(session.get('live', false)));
};

/**
 * For live followup sessions (e.g. monitor interface) there can be multiple recording files for a given channelId
 * They represent different recordings from the same feed, but we only want the latest recording to avoid display
 * multiple videos from the same feed
 */
export const getLatestRecordingFilePerChannel = (recordingFiles: ImmutableList): ImmutableList => {
    const recordingFilesJs = recordingFiles.toJS();
    // use map to preserve insertion order and thus the feed order
    const recordingFilesByChannelId = new Map();
    recordingFilesJs.forEach((rf) => {
        // set if not in the map, or if our startTs is later than the current item's startTs
        if (
            !recordingFilesByChannelId.has(rf.channelId) ||
            recordingFilesByChannelId.get(rf.channelId).startTs < rf.startTs
        ) {
            recordingFilesByChannelId.set(rf.channelId, rf);
        }
    });
    return Immutable.fromJS(Array.from(recordingFilesByChannelId.values()));
};

export const getSimulatorSessionFileId = (session) => {
    return session.get('recordings', Immutable.List()).reduce((a, r) => {
        const files = r.getIn(['vcRecording', 'simulatorSessionFiles'], Immutable.List());
        return a.concat(files);
    }, Immutable.List());
};

/**
 * This is only applies for live and when the session have not started yet.
 * It will be true only when you are picking inputs and configuring the session
 * before hitting start recording.
 */
export const isOnStartSessionScreen = (captureHost: ImmutableMap<unknown, unknown>): boolean => {
    return isNil(captureHost.get('session'));
};

const detectLocalStreamAvailability = async (servers: List<string>) => {
    const host = selectHost(servers);
    try {
        await axios({
            method: 'get',
            url: url.resolve(
                // @ts-expect-error TS2339: Property format does not exist on type "" | UrlWithStringQuery
                host.format(),
                '/capturenode/api/health',
            ),
            timeout: 100,
        });
        return true;
    } catch (err) {
        if (err && err.status === 500) {
            return true;
        }
        return false;
    }
};

export const getMaxInputsAllowed = (captureHost: CaptureHost): number => {
    return captureHost.getIn(['captureNode', 'recorderConfig', 'maxInputsAllowed'], DEFAULT_INPUTS_LIMIT);
};

const getDefaultSelectedInputs = (captureHost) => {
    let selectedRecordingFiles = Immutable.Map();
    const streamingChannels = getStreamingChannels(captureHost);

    streamingChannels.take(getMaxInputsAllowed(captureHost)).forEach((ch) => {
        selectedRecordingFiles = selectedRecordingFiles.set(ch.get('channelId'), true);
    });

    return selectedRecordingFiles;
};

/**
 * Used to setup the default selection of capture inputs.
 * We restrict to the maxInputs allowed and filter on only streaming streams.
 */
export const getDefaultSelectedCaptureInputs = (captureHost: CaptureHost) => {
    return getValidCaptureInputs(captureHost)
        .filter((ci) => ci.get('channels').every((ch) => ch.get('status') === 'streaming'))
        .take(getMaxInputsAllowed(captureHost));
};

/**
 * Used to setup the default selection of capture inputs.
 * We restrict to the maxInputs allowed and filter on only streaming streams.
 */
export const getDefaultSelectedCaptureInputs_SCLD17956 = (captureHost: CaptureHost) => {
    const validCaptureInputs = getValidCaptureInputs(captureHost);

    const streamingCaptureInputs = validCaptureInputs.filter((ci) =>
        ci.get('channels').every((ch) => ch.get('status') === 'streaming'),
    );

    const location = captureHost.get('location');
    const ipVideoFeeds = location.get('ipVideoFeeds', Immutable.List());

    // Sort feeds before getting subset
    const sortedCaptureInputs = streamingCaptureInputs.sortBy((ci) => {
        const captureInputId = ci.get('captureInputId');
        const ipVideoFeed = ipVideoFeeds.find((ivf) => ivf.get('captureInputId') === captureInputId);

        if (!ipVideoFeed) {
            return 0;
        }

        const xref_Locations_IpVideoFeeds = ipVideoFeed.get('xref_Locations_IpVideoFeeds');
        return xref_Locations_IpVideoFeeds.get('sort', 0);
    });

    return sortedCaptureInputs.take(getMaxInputsAllowed(captureHost));
};

/**
 * Used to convert the selected capture inputs into the format the ViewerInputs
 * component can ingest. Also, it removes channels that are no longer valid if they exist in the
 * previous selection. There must be some selection, so if it's now empty, we reset to the defaults.
 */
export const getSelectedRecordingFilesFromSelectedCaptureInputs = (
    captureHost: CaptureHost,
    selectedCaptureInputs: List<CaptureInput>,
): ImmutableMap<unknown, unknown> => {
    logger.info('[videoInfo] Getting selected recording files from selected capture inputs');
    let selectedRecordingFiles = Immutable.Map();
    const streamingChannels = getStreamingChannels(captureHost);

    selectedCaptureInputs
        .reduce((a, ci) => {
            return a.concat(ci.get('channels'));
        }, Immutable.List<Channel>())
        .filter((ch) => {
            const found = streamingChannels.find((c) => {
                return c.get('channelId') === ch.get('channelId');
            });
            return !isUndefined(found);
        })
        .forEach((c) => {
            selectedRecordingFiles = selectedRecordingFiles.set(c.get('channelId'), true);
        });

    if (selectedRecordingFiles.isEmpty()) {
        selectedRecordingFiles = getDefaultSelectedInputs(captureHost);
    }

    logger.info(
        '[videoInfo] Selected recording files from selected capture inputs:',
        JSON.stringify(selectedRecordingFiles.toJS()),
    );
    return selectedRecordingFiles;
};

export const getSelectedInputsFromRecordedFiles = (
    recordedFiles: List<ImmutableMap<unknown, unknown>>,
): ImmutableMap<string, boolean> => {
    let selectedRecordingFiles = Immutable.Map<string, boolean>();
    recordedFiles.forEach((recordedFile) => {
        selectedRecordingFiles = selectedRecordingFiles.set(
            // @ts-expect-error TS2345: Argument of type unknown is not assignable to parameter of type string
            recordedFile.get('channelId'),
            true,
        );
    });
    return selectedRecordingFiles;
};

export const createVideoInfoVisibilityFromSelected = (
    videoInfo: ImmutableMap<unknown, unknown>,
    selectedInfo: ImmutableMap<unknown, unknown>,
): ImmutableMap<unknown, unknown> => {
    return selectedInfo.reduce((m, s, k) => {
        // @ts-expect-error TS2339: Property find does not exist on type unknown
        const fileInfo = videoInfo.get('files', Immutable.List()).find((f) => f.get('videoId') === k);
        if (isNil(fileInfo)) {
            return m;
        }
        return m.set(fileInfo.get('videoId'), true);
    }, Immutable.Map());
};

export const getAllUnavailableInputNames = (captureHost: CaptureHost): List<string> => {
    return captureHost
        .getIn(['captureNode', 'captureInputs'], Immutable.List())
        .filter((ci) => !isValidCaptureInput(ci))
        .map((ci) => ci.getIn(['defaultChannelConfigurations', 'high', 'displayName']));
};

export type VideoInfoFile = MapOf<{
    captureNodeId: string;
    name: string;
    videoId: string;
    hasAudio: boolean;
    ptz?: Ptz;
    sort?: number;
    captureInputId?: unknown;
    vera?: unknown;
    livekit?: unknown;
    urls?: ImmutableMap<unknown, unknown>;
    startTs?: Date;
}>;

export type VideoInfo = MapOf<{
    live: boolean;
    menuEnabled: boolean;
    trackBarEnabled: boolean;
    webRtcSessionId: string;
    host: string;
    path?: string | undefined;
    port?: string | undefined;
    captureNodeId: string;
    files: List<VideoInfoFile>;
    filesOriginalOrder: List<VideoInfoFile>;
    visibility?: unknown;
    token?: string | undefined;
    // TODO SCLD-23638 This is only used for WebRTC video with a CaptureNode to correctyl order video feeds based on
    //  custom ip video feed orders with location based recording (LBR). This should be removed when WebRTCVideo is
    //  removed.
    captureInputOrderToFeedOrder?: number[];
}>;

/**
 * In addition to converting recorded files to videoInfo.
 * Extract the ptz info from the captureHost and add to the videoInfo based on channelId
 */
export const convertReadyRecordedFilesToVideoInfoLive = (
    recordedFiles: ImmutableList,
    recordedFilesOriginalOrder: ImmutableList,
    captureHost: CaptureHost,
): VideoInfo => {
    logger.info('[videoInfo] Converting ready recorded files to video info for live session');
    if (recordedFiles.isEmpty()) {
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type VideoInfo
        return Immutable.Map();
    }

    const channels = getRecordingChannels(captureHost);

    const promoteMetaToVideoInfoFile = (f: VideoInfoFile): VideoInfoFile => {
        const channel = channels.find((c) => c.get('channelId') === f.get('videoId'));
        if (!isNil(channel)) {
            const captureInputId = channel.get('captureInputId') as CaptureInput;
            return (
                f
                    .set('ptz', captureInputId.get('ptz'))
                    // TODO SCLD-17956 Does this sort value need to be updated?
                    .set('sort', captureInputId.get('sort'))
                    .set('captureInputId', channel.getIn(['captureInputId', 'captureInputId']))
            );
        }
        return f;
    };

    let videoInfo = createWebRTCVideoInfo(
        recordedFiles,
        recordedFilesOriginalOrder,
        captureHost.get('webRtcSessionId'),
        captureHost.get('captureNodeId'),
        captureHost.get('captureNodeId'),
    );

    videoInfo = videoInfo.set(
        'files',
        videoInfo
            .get('files')
            .map(promoteMetaToVideoInfoFile)
            .sortBy((f) => f.get('sort')),
    );
    videoInfo = videoInfo.set(
        'filesOriginalOrder',
        videoInfo
            .get('filesOriginalOrder')
            .map(promoteMetaToVideoInfoFile)
            .sortBy((f) => f.get('sort')),
    );

    return videoInfo;
};

export const convertReadyRecordedFilesToVideoInfoLive_SCLD17956 = (
    recordedFiles: ImmutableList,
    recordedFilesOriginalOrder: ImmutableList,
    captureHost: CaptureHost,
): VideoInfo => {
    logger.info('[videoInfo] Converting ready recorded files to video info for live session');
    if (recordedFiles.isEmpty()) {
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type VideoInfo
        return Immutable.Map();
    }

    const channels = getRecordingChannels(captureHost);
    const captureInputs = getValidCaptureInputs(captureHost);

    const captureInputByChannelId: {[channelId: string]: CaptureInput} = {};

    channels.forEach((channel) => {
        const channelId = channel.get('channelId');

        captureInputByChannelId[channelId] = captureInputs.find((ci) => {
            const captureInputChannels = ci.get('channels');
            const captureInputChannel = captureInputChannels.find((cic) => cic.get('channelId') === channelId);

            return !isNil(captureInputChannel);
        });
    });

    const promoteMetaToVideoInfoFile = (f: VideoInfoFile): VideoInfoFile => {
        const channel = channels.find((c) => c.get('channelId') === f.get('videoId'));
        if (!isNil(channel)) {
            const captureInputId = channel.get('captureInputId') as CaptureInput;
            return (
                f
                    .set('ptz', captureInputId.get('ptz'))
                    // TODO SCLD-17956 Does this sort value need to be updated?
                    .set('sort', captureInputId.get('sort'))
                    .set('captureInputId', channel.getIn(['captureInputId', 'captureInputId']))
            );
        }
        return f;
    };

    let videoInfo = createWebRTCVideoInfo(
        recordedFiles,
        recordedFilesOriginalOrder,
        captureHost.get('webRtcSessionId'),
        captureHost.get('captureNodeId'),
        captureHost.get('captureNodeId'),
    );

    const files = videoInfo
        .get('files')
        .map(promoteMetaToVideoInfoFile)
        .sortBy((f) => {
            const channelId = f.get('videoId');
            const captureInput = captureInputByChannelId[channelId];

            if (isNil(captureInput)) {
                console.error(`No capture input found for ${channelId}, using default sort value`);
                return f.get('sort');
            }

            const captureInputId = captureInput.get('captureInputId');

            const location = captureHost.get('location');
            const ipVideoFeeds = location.get('ipVideoFeeds');
            const ipVideoFeed = ipVideoFeeds.find((ivf) => ivf.get('captureInputId') === captureInputId);

            if (ipVideoFeed) {
                const xref_Locations_IpVideoFeeds = ipVideoFeed.get('xref_Locations_IpVideoFeeds');
                return xref_Locations_IpVideoFeeds.get('sort');
            }

            console.error(`No ip video feed found for ${captureInputId}, using default sort value`);

            return f.get('sort');
        });

    const filesOriginalOrder = videoInfo
        .get('filesOriginalOrder')
        .map(promoteMetaToVideoInfoFile)
        .sortBy((f) => {
            const channelId = f.get('videoId');
            const captureInput = captureInputByChannelId[channelId];

            if (isNil(captureInput)) {
                return f.get('sort');
            }

            const captureInputId = captureInput.get('captureInputId');

            const location = captureHost.get('location');
            const ipVideoFeeds = location.get('ipVideoFeeds');
            const ipVideoFeed = ipVideoFeeds.find((ivf) => ivf.get('captureInputId') === captureInputId);

            if (ipVideoFeed) {
                const xref_Locations_IpVideoFeeds = ipVideoFeed.get('xref_Locations_IpVideoFeeds');
                return xref_Locations_IpVideoFeeds.get('sort');
            }

            return f.get('sort');
        });

    videoInfo = videoInfo.set('files', files);
    videoInfo = videoInfo.set('filesOriginalOrder', filesOriginalOrder);

    // TODO SCLD-23638 This is only used for WebRTC video with a CaptureNode to correctyl order video feeds based on
    //  custom ip video feed orders with location based recording (LBR). This should be removed when WebRTCVideo is
    //  removed.
    const captureInputOrderToFeedOrder = getCaptureInputOrderToFeedOrder(captureHost, files, captureInputByChannelId);
    videoInfo = videoInfo.set('captureInputOrderToFeedOrder', captureInputOrderToFeedOrder);

    return videoInfo;
};

export const convertReadyRecordedFilesToVideoInfo = async (recordedFiles, recordedFilesOriginalOrder) => {
    return recordedFiles.isEmpty()
        ? Immutable.Map()
        : await createHTMLVideoInfo(recordedFiles, recordedFilesOriginalOrder);
};

const createWebRTCVideoInfo = (
    recordedFiles,
    recordedFilesOriginalOrder,
    webRtcSessionId: string,
    host: string,
    captureNodeId: string,
): VideoInfo => {
    logger.info('[videoInfo] Creating WebRTC video info');
    return Immutable.Map({
        filesOriginalOrder: recordedFilesOriginalOrder.map((r) => {
            return Immutable.Map({
                captureNodeId: r.get('captureNodeId'),
                name: r.get('name'),
                videoId: isNil(r.get('channelId')) ? r.get('recordedFileId') : r.get('channelId'),
                startTs: r.get('startTs'),
                hasAudio: r.get('hasAudio'),
            });
        }),
        files: recordedFiles.map((r) => {
            return Immutable.Map({
                captureNodeId: r.get('captureNodeId'),
                name: r.get('name'),
                videoId: isNil(r.get('channelId')) ? r.get('recordedFileId') : r.get('channelId'),
                startTs: r.get('startTs'),
                hasAudio: r.get('hasAudio'),
            });
        }),
        live: true,
        menuEnabled: false,
        trackBarEnabled: false,
        webRtcSessionId,
        host,
        captureNodeId,
    });
};

const getRecordedFilesForHTMLVideo = async (recordedFile) => {
    return {
        urls: {
            // Local HTTP is no longer supported
            remoteHttp: recordedFile.get('remoteHttp'),
            remoteHls: recordedFile.get('remoteHls'),
        },
        name: recordedFile.get('name'),
        videoId: isNil(recordedFile.get('channelId'))
            ? recordedFile.get('recordedFileId')
            : recordedFile.get('channelId'),
        recordedFileId: recordedFile.get('recordedFileId'),
        startTs: recordedFile.get('startTs'),
    };
};

export const createHTMLVideoInfo = async (recordedFiles, recordedFilesOriginalOrder) => {
    const nonDeletedVideos = recordedFiles.filter((r) => isNil(r.get('cloudDeleteRequestTs')));
    const nonDeletedVideosOriginalOrder = recordedFilesOriginalOrder.filter((r) =>
        isNil(r.get('cloudDeleteRequestTs')),
    );

    const files = await Promise.all(
        nonDeletedVideos.map((recordedFile) => getRecordedFilesForHTMLVideo(recordedFile)).toArray(),
    );
    const filesOriginalOrder = await Promise.all(
        nonDeletedVideosOriginalOrder.map((recordedFile) => getRecordedFilesForHTMLVideo(recordedFile)).toArray(),
    );

    return Immutable.fromJS({
        filesOriginalOrder,
        files,
        live: false,
        menuEnabled: false,
        trackBarEnabled: false,
    });
};

export const updateFilesSortOrder = (
    videoInfo: VideoInfo,
    sortedRecordingFiles: SessionViewerStore['recordingFiles'],
) => {
    let sortedFiles = Immutable.List<VideoInfoFile>();

    sortedRecordingFiles.forEach((r) => {
        const file = videoInfo.get('files').find(
            (f) => f.get('videoId') === (isNil(r.get('channelId')) ? r.get('recordedFileId') : r.get('channelId')),
            this,
            // @ts-expect-error TS2345: Argument of type Map<unknown, unknown> is not assignable to parameter of type VideoInfoFile
            Immutable.Map(),
        );
        if (!file.isEmpty()) {
            sortedFiles = sortedFiles.push(file);
        }
    });

    return videoInfo.set('files', sortedFiles);
};

// TODO SCLD-23638 This is only used for WebRTC video with a CaptureNode to correctyl order video feeds based on
//  custom ip video feed orders with location based recording (LBR). This should be removed when WebRTCVideo is
//  removed.
/**
 * @returns An array that functions as a lookup from capture input sort values to user-defined location-based recording
 * sort values.
 */
const getCaptureInputOrderToFeedOrder = (
    captureHost: CaptureHost,
    files:
        | List<VideoInfoFile>
        | List<
              MapOf<{
                  captureNodeId: string;
                  name: string;
                  url?: string;
                  videoId: string;
                  ptz: unknown;
                  hasAudio: boolean;
                  captureInputId: string;
              }>
          >,
    captureInputByChannelId: {[channelId: string]: CaptureInput},
) => {
    return (
        files
            // Map video files to sort values using the same logic as other utils that create video info objects.
            // This mapping returns the sort values provided for the capture input and the custom sort value defined only
            // for location-based recording (LBR)
            .map((file) => {
                const channelId = file.get('videoId');
                const captureInput = captureInputByChannelId[channelId];

                if (isNil(captureInput)) {
                    return {
                        captureInputSort: 0,
                        sort: 0,
                    };
                }

                const captureInputId = captureInput.get('captureInputId');
                const captureInputSort = captureInput.get('sort', 0);

                const location = captureHost.get('location');
                const ipVideoFeeds = location.get('ipVideoFeeds');
                const ipVideoFeed = ipVideoFeeds.find((ivf) => ivf.get('captureInputId') === captureInputId);

                if (ipVideoFeed) {
                    const xref_Locations_IpVideoFeeds = ipVideoFeed.get('xref_Locations_IpVideoFeeds');

                    return {
                        captureInputSort,
                        sort: xref_Locations_IpVideoFeeds.get('sort'),
                    };
                }

                return {
                    captureInputSort,
                    sort: captureInputSort,
                };
            })
            .toJS()
            // The return value will be user defined sort values ordered by the capture input sort values. This can be used
            // as a lookup from capture input sort values to LBR ip video feed sort values.
            .toSorted((a, b) => a.captureInputSort - b.captureInputSort)
            .map(({sort}) => sort)
    );
};

export const convertCaptureHostToVideoInfo_SCLD17956 = (
    captureHost: CaptureHost,
    monitorControlEnabled: boolean = false,
): VideoInfo => {
    logger.info('[videoInfo] Converting capture host to video info');
    const channels = getStreamingChannels(captureHost);
    logger.info('[videoInfo] Streaming channels:', JSON.stringify(channels.toJS()));
    const captureInputs = getValidCaptureInputs(captureHost);
    logger.info('[videoInfo] Valid capture inputs:', JSON.stringify(captureInputs.toJS()));

    if (channels.isEmpty()) {
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type VideoInfo
        return Immutable.Map();
    }

    let server: string | UrlWithStringQuery;
    let files: List<
        MapOf<{
            captureNodeId: string;
            name: string;
            url?: string;
            videoId: string;
            ptz: unknown;
            hasAudio: boolean;
            captureInputId: string;
        }>
    >;
    let token: string;

    const localRtmp = channels.first().get('localRtmp');

    const captureInputByChannelId: {[channelId: string]: CaptureInput} = {};

    channels.forEach((channel) => {
        const channelId = channel.get('channelId');

        captureInputByChannelId[channelId] = captureInputs.find((ci) => {
            const captureInputChannels = ci.get('channels');
            const captureInputChannel = captureInputChannels.find((cic) => cic.get('channelId') === channelId);

            return !isNil(captureInputChannel);
        });
    });

    if (!isEmpty(localRtmp)) {
        logger.info('[videoInfo] Setting video info with local RTMP');
        server = selectHost(localRtmp.map((s) => s.get('rtmpServerPath')));
        token = localRtmp.first().get('rtmpStreamToken');
        files = channels.map((c) => {
            const channelId = c.get('channelId');
            const captureInput = monitorControlEnabled ? captureInputByChannelId[channelId] : null;

            return Immutable.Map({
                captureNodeId: captureHost.get('captureNodeId'),
                name: c.getIn(['configuration', 'displayName']),
                url: url.parse(c.get('localRtmp').first().get('rtmpStreamName')).pathname,
                videoId: channelId,
                ptz: isNil(captureInput) ? c.get('ptz') : captureInput.get('ptz'),
                hasAudio: c.getIn(['captureInputId', 'audioStatus']) === 'ready',
                captureInputId: c.getIn(['captureInputId', 'captureInputId']),
            });
        });
    } else {
        logger.info('[videoInfo] not using local RTMP');
        files = channels.map((c) => {
            const channelId = c.get('channelId');
            const captureInput = monitorControlEnabled ? captureInputByChannelId[channelId] : null;

            return Immutable.Map({
                captureNodeId: captureHost.get('captureNodeId'),
                name: c.getIn(['configuration', 'displayName']),
                videoId: channelId,
                ptz: isNil(captureInput) ? c.get('ptz') : captureInput.get('ptz'),
                hasAudio: c.getIn(['captureInputId', 'audioStatus']) === 'ready',
                captureInputId: c.getIn(['captureInputId', 'captureInputId']),
            });
        });
    }

    const sortedFiles = files.sortBy((f) => {
        const channelId = f.get('videoId');
        const captureInput = captureInputByChannelId[channelId];

        if (isNil(captureInput)) {
            return 0;
        }

        const captureInputId = captureInput.get('captureInputId');

        const location = captureHost.get('location');
        const ipVideoFeeds = location.get('ipVideoFeeds');
        const ipVideoFeed = ipVideoFeeds.find((ivf) => ivf.get('captureInputId') === captureInputId);

        if (ipVideoFeed) {
            const xref_Locations_IpVideoFeeds = ipVideoFeed.get('xref_Locations_IpVideoFeeds');
            return xref_Locations_IpVideoFeeds.get('sort');
        }

        // This is a static value that cannot be modified by simcapture. It should only be used as a defensive fallback.
        return captureInput.get('sort', 0);
    });
    logger.info('[videoInfo] Video info files:', JSON.stringify(sortedFiles.toJS()));

    const captureInputOrderToFeedOrder = getCaptureInputOrderToFeedOrder(captureHost, files, captureInputByChannelId);

    // @ts-expect-error TS2322: Type is not assignable to type VideoInfo
    return Immutable.Map({
        webRtcSessionId: captureHost.get('webRtcSessionId'),
        host: get(server, 'hostname', captureHost.get('captureNodeId')),
        path: get(server, 'pathname', undefined),
        port: get(server, 'port', undefined),
        token,
        // TODO This comment seems like it is not true since this is an immutable list and cannot be modified.
        filesOriginalOrder: sortedFiles.map((f) => f), // Required to create a new instance so when modifying the sort the original order is maintained
        files: sortedFiles,
        live: true,
        menuEnabled: false,
        trackBarEnabled: false,
        captureNodeId: captureHost.get('captureNodeId'),
        captureInputOrderToFeedOrder,
    });
};

export const convertCaptureHostToVideoInfo = (
    captureHost: CaptureHost,
    monitorControlEnabled: boolean = false,
): VideoInfo => {
    logger.info('[videoInfo] Converting capture host to video info');
    const channels = getStreamingChannels(captureHost);
    logger.info('[videoInfo] Streaming channels:', JSON.stringify(channels.toJS()));
    const captureInputs = getValidCaptureInputs(captureHost);
    logger.info('[videoInfo] Valid capture inputs:', JSON.stringify(captureInputs.toJS()));
    if (!channels.isEmpty()) {
        let server: string | UrlWithStringQuery;
        let files: List<
            MapOf<{
                captureNodeId: string;
                name: string;
                url?: string;
                videoId: string;
                ptz: unknown;
                hasAudio: boolean;
                captureInputId: string;
            }>
        >;
        let token: string | undefined;

        const localRtmp = channels.first().get('localRtmp');
        if (!isEmpty(localRtmp)) {
            logger.info('[videoInfo] Setting video info with local RTMP');
            server = selectHost(localRtmp.map((s) => s.get('rtmpServerPath')));
            token = localRtmp.first().get('rtmpStreamToken');
            files = channels.map((c) => {
                const captureInput = monitorControlEnabled
                    ? captureInputs.find(
                          (ci) => !isNil(ci.get('channels').find((ch) => ch.get('channelId') === c.get('channelId'))),
                      )
                    : null;
                return Immutable.Map({
                    captureNodeId: captureHost.get('captureNodeId'),
                    name: c.getIn(['configuration', 'displayName']),
                    url: url.parse(c.get('localRtmp').first().get('rtmpStreamName')).pathname,
                    videoId: c.get('channelId'),
                    ptz: isNil(captureInput) ? c.get('ptz') : captureInput.get('ptz'),
                    hasAudio: c.getIn(['captureInputId', 'audioStatus']) === 'ready',
                    captureInputId: c.getIn(['captureInputId', 'captureInputId']),
                });
            });
        } else {
            logger.info('[videoInfo] not using local RTMP');
            files = channels.map((c) => {
                const captureInput = monitorControlEnabled
                    ? captureInputs.find(
                          (ci) => !isNil(ci.get('channels').find((ch) => ch.get('channelId') === c.get('channelId'))),
                      )
                    : null;
                return Immutable.Map({
                    captureNodeId: captureHost.get('captureNodeId'),
                    name: c.getIn(['configuration', 'displayName']),
                    videoId: c.get('channelId'),
                    ptz: isNil(captureInput) ? c.get('ptz') : captureInput.get('ptz'),
                    hasAudio: c.getIn(['captureInputId', 'audioStatus']) === 'ready',
                    captureInputId: c.getIn(['captureInputId', 'captureInputId']),
                });
            });
        }
        files = files.sortBy((f) => {
            const captureInput = captureInputs.find(
                (ci) => !isNil(ci.get('channels').find((ch) => ch.get('channelId') === f.get('videoId'))),
            );
            if (isNil(captureInput)) {
                return 0;
            }
            return captureInput.get('sort', 0);
        });
        logger.info('[videoInfo] Video info files:', JSON.stringify(files.toJS()));

        // @ts-expect-error TS2322: Type is not assignable to type VideoInfo
        return Immutable.Map({
            webRtcSessionId: captureHost.get('webRtcSessionId'),
            host: get(server, 'hostname', captureHost.get('captureNodeId')),
            path: get(server, 'pathname', undefined),
            port: get(server, 'port', undefined),
            token,
            filesOriginalOrder: files.map((f) => f), // Required to create a new instance so when modifying the sort the original order is maintained
            files: files,
            live: true,
            menuEnabled: false,
            trackBarEnabled: false,
            captureNodeId: captureHost.get('captureNodeId'),
        });
    }

    // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type VideoInfo
    return Immutable.Map();
};

/**
 * When we have a session with multiple recordings, we need to get the durations of
 * specific recordings rather than from the session or from the first recording.
 * @returns Defaults to 0 for an empty recording
 */
export const getDurationSecondsFromVcRecording = (vcRecording = Immutable.Map()): number => {
    let result = 0;
    if (!vcRecording.isEmpty()) {
        const recordedFiles = vcRecording.get('recordedFiles', Immutable.List());

        // @ts-expect-error TS2339: Property isEmpty does not exist on type unknown
        if (!recordedFiles.isEmpty()) {
            // @ts-expect-error TS2339: Property reduce does not exist on type unknown
            result = recordedFiles.reduce((acc, rf) => {
                const startTs = rf.get('startTs');
                const stopTs = rf.get('stopTs');
                const cloudDeleteRequestTs = rf.get('cloudDeleteRequestTs');

                if (isNil(cloudDeleteRequestTs) && !isNil(startTs) && !isNil(stopTs) && stopTs !== startTs) {
                    const duration = (stopTs - startTs) / 1000;
                    if (duration > acc) {
                        return duration;
                    }
                }
                return acc;
            }, 0);
        }
    }
    return result;
};

/**
 * Deprecated. Duration should ideally be calculated one of two ways
 * 1. The session has multiple recordings, so each recording is calculated individually
 * 2. The session has one recording and we use the session's startTs and endTs
 * This function is a fallback if neither are true
 */
export const getFirstRecordingDurationSeconds = (session: ImmutableMap<unknown, unknown> = Immutable.Map()): number => {
    const vcRecording = session.getIn(['recordings', 0, 'vcRecording'], Immutable.Map<unknown, unknown>());
    return getDurationSecondsFromVcRecording(
        // @ts-expect-error TS2345: Argument of type unknown is not assignable to parameter of type Map<unknown, unknown>
        vcRecording,
    );
};

/**
 * Returns a session's duration or a fallback.
 */
export const getDuration = (videoDuration?: number, recordingDuration?: number): number => {
    let result = 0;

    if (!isNil(videoDuration) && videoDuration > 0) {
        result = videoDuration;
    } else if (!isNil(recordingDuration) && recordingDuration > 0) {
        result = recordingDuration;
    }
    return result;
};

// Only check if the video feeds have changed if the host has changed.
export const haveVideoFeedsChanged = (nextData, prevData): boolean => {
    if (nextData.get('host') !== prevData.get('host')) {
        const prevFiles = prevData.get('files', Immutable.List());
        const nextFiles = nextData.get('files', Immutable.List());
        return (
            prevFiles.size !== nextFiles.size ||
            nextFiles.every((f) => isNil(prevFiles.find((d) => d.get('videoId') === f.get('videoId'))))
        );
    }
    return false;
};

export const getDirectStreamInfoFromCaptureHost = (captureHost: CaptureHost | FetchCaptureHostResponse) => {
    return Immutable.Map({
        captureNodeId: captureHost.getIn(['captureNode', 'captureNodeId']),
        hostNames: captureHost.getIn(['captureNode', 'systemInfo', 'hostNames']),
        // @ts-expect-error error TS2554: Expected 2 arguments, but got 1.
        directStreamToken: captureHost.get('directStreamToken'),
        captureHost,
    });
};
